import { cloneElement, ReactElement, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { createSearchParams, Link, useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, EyeOutlined, SelectOutlined, UploadOutlined } from '@ant-design/icons';
import { Bundle, SubscriptionStatus } from '@prisma/client';
import {
  Breadcrumb,
  Button,
  Dropdown,
  FloatButton,
  Input,
  Layout,
  MenuProps,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Tour,
} from 'antd';

import { Role } from '@app/domain-interfaces';
import { FindSubscriptionsResultData } from '@app/domain-subscription-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { useFindBundle, useFindProductFamilies } from '../../bundles';
import { ProductFamilyHierarchyPopover } from '../../bundles/modals';
import { ActiveColumn, AuditFilters, CreatedByColumn, UpdatedByColumn } from '../../common';
import { useSubscriptionStatusTag } from '../atoms/subscription-status-renderer';
import { useCancelSubscription, useFindSubscription } from '../data';
import { useSubscriptionPermissions } from '../forms/rules/use-subscription-permissions';
import { DetailModal } from '../modals';

export const SUBSCRIPTIONS_LANDING_TITLE = 'Bundle Subscriptions';

export const LandingPage = () => {
  const cancelSubscription = useCancelSubscription();
  const [messageApi, messageContextHolder] = message.useMessage();
  const navigate = useNavigate();
  // toggle to show/hide the delete dialog
  const [showDetail, setShowDetail] = useState<number | undefined>();
  const { user, canBulkSubscribe, canEdit } = useSubscriptionPermissions();

  const createButtonRef = useRef<HTMLButtonElement>(null);
  const bulkButtonRef = useRef<HTMLButtonElement>(null);

  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const detailsButtonRef = useRef<HTMLButtonElement>(null);
  const productFamilyButtonRef = useRef<HTMLButtonElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const subscriptionStatusTagRef = useRef<any>();

  const bundlesQuery = useFindBundle<Bundle>({
    where: { subscriptions: { some: {} } },
    select: { id: true, name: true },
    orderBy: { name: 'asc' },
  });
  const productFamilyQuery = useFindProductFamilies({
    where: { bundles: { some: { subscriptions: { some: {} } } } },
    orderBy: { name: 'asc' },
  });

  const actions: MenuProps['items'] = [
    {
      label: 'Edit',
      key: 'edit',
      icon: <EditOutlined />,
    },
    {
      label: 'Subscribe To',
      key: 'subscribe to',
      icon: <SelectOutlined />,
    },
    ...(user?.roles[Role.BUNDLE_ADMIN]
      ? [
          {
            label: 'Bulk Subscribe To',
            key: 'bulk subscribe to',
            icon: <UploadOutlined />,
          },
        ]
      : []),
    {
      label: 'Cancel',
      key: 'cancel',
      icon: <DeleteOutlined />,
      danger: true,
    },
  ];

  const handleActionMenuClick = (e: { key: string }, record: FindSubscriptionsResultData) => {
    switch (e.key) {
      case 'edit':
        navigate(`./${record.id}`);
        break;
      case 'subscribe to':
        navigate({
          pathname: './create',
          search: createSearchParams({
            bundleid: record.bundleId.toString(),
          }).toString(),
        });
        break;
      case 'bulk subscribe to':
        navigate({
          pathname: '../subscriptions/bulk',
          search: createSearchParams({ bundleid: record.bundleId.toString() }).toString(),
        });
        break;
      case 'cancel':
        Modal.confirm({
          title: 'Cancel subscription for bundle',
          centered: true,
          content: `Are you sure you want to cancel subscription for user '${record.user.name}' and bundle '${record.bundle.name}'.?`,
          onOk: () => {
            cancelSubscription.mutate(record.id, {
              onSuccess: () => {
                messageApi.open({
                  type: 'success',
                  content: `Successfully cancelled subscription for user '${record.user.name}' and bundle '${record.bundle.name}'.`,
                });
              },
              onError: (error) => {
                messageApi.open({
                  type: 'error',
                  content: `Failed to cancelled subscription for user '${record.user.name}' and bundle '${record.bundle.name}'.. Error: ${error}`,
                });
              },
            });
          },
        });
        break;
      default:
        console.log(`Cannot handle key: ${e.key}.`);
        break;
    }
  };

  const { getProps: getSubscriptionStatusTagProps } = useSubscriptionStatusTag();

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
    getTourFloatButtonProps,
    getTourProps,
  } = Tables.useTableState<FindSubscriptionsResultData>(
    'Subscription',
    [
      {
        title: 'Action',
        width: 140,
        render: (_, record, index) => (
          <Dropdown.Button
            size="small"
            onClick={() => setShowDetail(record.id)}
            menu={{
              items: actions.filter(
                (action) =>
                  (action?.key !== 'edit' && action?.key !== 'cancel' && action?.key !== 'bulk subscribe to') ||
                  (action?.key === 'edit' && canEdit(record)) ||
                  (action?.key === 'bulk subscribe to' && canBulkSubscribe(record)) ||
                  (action?.key === 'cancel' && record.status?.isActive && canEdit(record)),
              ),
              onClick: (e) => handleActionMenuClick(e, record),
            }}
            buttonsRender={(buttons) =>
              index === 0
                ? buttons.map((button, index) =>
                    index === 0
                      ? cloneElement(button as ReactElement, {
                          ...(button as ReactElement).props,
                          ref: detailsButtonRef,
                        })
                      : cloneElement(button as ReactElement, {
                          ...(button as ReactElement).props,
                          ref: actionButtonRef,
                        }),
                  )
                : buttons
            }
          >
            <Space>
              <EyeOutlined />
              Details
            </Space>
          </Dropdown.Button>
        ),
      },
      { ...ActiveColumn, dataIndex: ['status', 'isActive'] },
      {
        title: 'Bundle',
        dataIndex: ['bundle', 'name'],
        width: 350,
        ellipsis: true,
        sorter: true,
        filters: bundlesQuery.data?.data.map(({ name }) => ({ text: name, value: name })),
        filterSearch: true,
      },
      {
        title: 'User',
        dataIndex: ['user', 'name'],
        width: 200,
        ellipsis: true,
        sorter: true,
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search users'),
      },
      {
        title: 'Product Family',
        dataIndex: ['bundle', 'productFamily', 'name'],
        width: 350,
        ellipsis: true,
        responsive: ['md'],
        sorter: true,
        render: (name: string, detail, index) => {
          return user?.roles[Role.BUNDLE_ADMIN] || user?.roles[Role.RULE_ADMIN] ? (
            <ProductFamilyHierarchyPopover
              productFamilyId={detail.bundle.productFamily?.id}
              productFamilyName={detail.bundle.productFamily?.name}
            >
              <Button
                ref={index === 0 ? productFamilyButtonRef : undefined}
                type="link"
                size="small"
                style={{ padding: 0 }}
              >
                {name}
              </Button>
            </ProductFamilyHierarchyPopover>
          ) : (
            name
          );
        },
        filters: productFamilyQuery.data?.data
          ?.map(({ name }) => ({ text: name, value: name }))
          .sort(({ text: a }, { text: b }) => (a < b ? -1 : a > b ? 1 : 0)), // server side sort isn't working for this
        filterSearch: true,
      },
      {
        title: 'Status',
        dataIndex: ['status', 'status'],
        width: 150,
        responsive: ['md'],
        sorter: true,
        render: (status: SubscriptionStatus, _, index) => (
          <Tag ref={index === 0 ? subscriptionStatusTagRef : undefined} {...getSubscriptionStatusTagProps(status)} />
        ),
        filters: [
          { text: 'Queued', value: SubscriptionStatus.QUEUED },
          { text: 'Pending', value: SubscriptionStatus.PENDING },
          { text: 'Completed', value: SubscriptionStatus.COMPLETED },
          { text: 'Error', value: SubscriptionStatus.ERROR },
          { text: 'Not Acquired', value: SubscriptionStatus.NOT_ACQUIRED },
        ],
      },
      { ...UpdatedByColumn, responsive: ['xxl'] },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 200,
        ellipsis: true,
        responsive: ['xxl'],
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      { ...CreatedByColumn, responsive: ['xxl'] },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 200,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend',
      },
    ],
    {
      customFilters: {
        'user.name': (value) => ({ user: { name: { contains: value ?? '', mode: 'insensitive' } } }),
        ...AuditFilters,
      },
      globalSearchFields: ['bundle.name', 'user.name', 'bundle.productFamily.name'],
    },
  );

  const queryResult = useFindSubscription<FindSubscriptionsResultData>(
    getQuery({
      include: {
        user: true,
        bundle: { include: { owners: true, productFamily: true } },
        status: true,
        createdByUser: true,
        updatedByUser: true,
      },
    }),
  );

  const tourProps = getTourProps([
    {
      title: 'Subscription Details',
      description: (
        <>
          Click on "Details" button to view additional information about the subscription.
          <ul>
            <li>Justification given, if any.</li>
            <li>Status of subscribed accesses. You can trigger retries here.</li>
            <li>Bundle details.</li>
          </ul>
        </>
      ),
      target: () => detailsButtonRef.current as HTMLButtonElement,
    },
    {
      title: 'Subscription Actions',
      description: (
        <>
          Hover over the "..." button to view the available actions for the subscription.
          <ul>
            <li>
              Click on the "Edit" option to modify this subscription. This is only available for subscriptions you
              created, where you are the subscriber, or where you are the bundle owner.
            </li>
            <li>
              Click the "Subscribe To" button to submit a new subscription with the same bundle as this subscription.
            </li>
            <li>
              Click on the "Cancel" option to cancel this subscription. This will unsubscribe you from the bundle. This
              is only available for subscriptions you created, where you are the subscriber, or where you are the bundle
              owner.
            </li>
          </ul>
        </>
      ),
      target: () => actionButtonRef.current as HTMLButtonElement,
    },
    ...(user?.roles[Role.BUNDLE_ADMIN] || user?.roles[Role.RULE_ADMIN]
      ? [
          {
            title: 'Product Family',
            description: 'Hover over the product family name to view the product family hierarchy.',
            target: () => productFamilyButtonRef.current as HTMLButtonElement,
          },
        ]
      : []),
    {
      title: 'Subscription Status',
      description: (
        <>
          A subscription can be in one of four statuses:
          <ul>
            <li>
              <strong>Queued:</strong> The subscription has been requested or has been retried, but no actions have been
              taken yet.
            </li>
            <li>
              <strong>Pending:</strong> At least one access request is waiting for approval. Click on the question mark
              to learn more about the approval process.
            </li>
            <li>
              <strong>Completed:</strong> All accesses requests have been resolved. They may or may not have been
              approved.
            </li>
            <li>
              <strong>Error:</strong> An error has occurred during the request process. See the subscription's details
              for more information and for the ability to retry to the request.
            </li>
            <li>
              <strong>Not Acquired:</strong> At least one access request has been rejected, cancelled, or expired in
              AGS. See the subscription details for more information and to retry the request.
            </li>
          </ul>
        </>
      ),
      target: () => subscriptionStatusTagRef.current as HTMLElement,
    },
  ]);

  const createStep = tourProps.steps?.find((x) => x.title === 'Create');
  if (createStep) {
    createStep.target = () => createButtonRef.current as HTMLButtonElement;
  }

  if (user?.roles[Role.BUNDLE_ADMIN]) {
    tourProps.steps?.splice(2, 0, {
      title: 'Bulk Upload',
      description:
        'Hover over the ellipsis button and click on the "Bulk Upload Subscriptions" option to use the bulk upload feature for subscriptions.',
      target: () => bulkButtonRef.current as HTMLButtonElement,
    });
  }

  return (
    <>
      <DetailModal subscriptionId={showDetail} onClose={() => setShowDetail(undefined)} />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: SUBSCRIPTIONS_LANDING_TITLE }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title={SUBSCRIPTIONS_LANDING_TITLE} />
        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} />,
              user?.roles[Role.BUNDLE_ADMIN] ? (
                <Dropdown.Button
                  {...getCreateButtonProps()}
                  icon={undefined}
                  menu={{
                    items: [{ key: 'bulk', label: 'Bulk Upload Subscriptions' }],
                    onClick: (e) => {
                      e.key === 'bulk' && navigate('bulk');
                    },
                  }}
                  buttonsRender={(buttons) =>
                    buttons.map((button, index) =>
                      index === 0
                        ? cloneElement(button as ReactElement, {
                            ...(button as ReactElement).props,
                            ref: createButtonRef,
                          })
                        : cloneElement(button as ReactElement, {
                            ...(button as ReactElement).props,
                            ref: bulkButtonRef,
                          }),
                    )
                  }
                />
              ) : (
                <Button {...getCreateButtonProps()} />
              ),
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />,
            ],
          })}
        />

        <Table {...getTableProps(queryResult)} />
        <Tour {...tourProps} />
        <FloatButton {...getTourFloatButtonProps()} />
        {messageContextHolder}
      </Layout.Content>
    </>
  );
};
